import React, { useEffect, useState, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { CircularProgress, Box } from '@mui/material'
import { supabase } from '../../config/supabase'

/**
 * ProtectedRoute - Guards routes that require authentication
 * 
 * IMPORTANT: Once authenticated, we NEVER unmount the Outlet to prevent
 * losing form state during token refreshes or auth state fluctuations.
 * We only redirect to login on explicit sign out.
 */
export default function ProtectedRoute() {
  const location = useLocation()
  
  // Three states: 'checking' | 'authenticated' | 'unauthenticated'
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking')
  
  // Once true, stays true until explicit sign out - prevents unmounting children
  const confirmedAuthenticated = useRef(false)

  useEffect(() => {
    let isMounted = true
    
    // Check initial auth state
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        
        if (!isMounted) return
        
        if (session) {
          confirmedAuthenticated.current = true
          setAuthState('authenticated')
        } else {
          setAuthState('unauthenticated')
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        if (isMounted) {
          setAuthState('unauthenticated')
        }
      }
    }

    checkAuth()

    // Listen for auth changes - but ONLY react to explicit sign out
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return
      
      console.log('Auth event:', event, 'Has session:', !!session)
      
      // ONLY sign out on explicit SIGNED_OUT event
      if (event === 'SIGNED_OUT') {
        console.log('Explicit sign out detected')
        confirmedAuthenticated.current = false
        setAuthState('unauthenticated')
        return
      }
      
      // If we get a valid session, confirm authentication
      if (session && !confirmedAuthenticated.current) {
        confirmedAuthenticated.current = true
        setAuthState('authenticated')
      }
      
      // IGNORE all other events that might have null session
      // (TOKEN_REFRESHED, INITIAL_SESSION, USER_UPDATED, etc.)
      // Once confirmed, we stay confirmed until explicit sign out
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Still checking initial auth - show spinner
  if (authState === 'checking') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // Not authenticated and never was - redirect to login
  if (authState === 'unauthenticated' && !confirmedAuthenticated.current) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Authenticated (or was authenticated) - render children
  // The key point: we ALWAYS render Outlet once confirmed, even if auth state flickers
  return <Outlet />
}
